import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import {
    CustomerIdTypeEnum,
    FundTypeEnum,
    GoldTypeEnum,
    ModuleEnum,
    PermissionsEnum,
    SilverTypeEnum,
    TransactionActionEnum,
    TransactionStatusEnum,
} from '~utils/enums';

import { TUpdateTransactionStatusReduxParams } from '~types/feat/approval';

import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';

import UtilFunctions from '~utils/functions';
import * as Styles from './_goldTransactionApprovalTable.module.scss';
import { TTransactionListing } from '~types/feat/gold';

const GoldTransactionTableApproval = () => {
    const dispatch = useAppDispatch();

    const {
        Misc: {
            renderTransactionStatusChip,
            fixedDecimal,
            renderBinaryStatusChip,
        },
    } = UtilFunctions;

    const transactionListing = useAppSelector(state => state.approval.pendingTransactionListing.data);
    const getPendingTransactionListingIsLoading = useAppSelector(state => state.approval.loadings.getPendingTransactionListing);
    const access = useAppSelector(state => state.auth.userDetails.access);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const setTempUpdateTransactionState = (data: TUpdateTransactionStatusReduxParams) => dispatch(combinedActions.approvalSetTempUpdateTransactionState(data));
    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.approvalSetConfirmUpdateTransactionStatusModalIsOpen(open));

    const setTransactionProductListingModalIsOpen = (state: boolean) => dispatch(combinedActions.goldSetTransactionProductListingModalIsOpen(state));
    const setSelectedGoldTransaction = (transaction: TTransactionListing) => dispatch(combinedActions.goldSetSelectedTransaction(transaction));

    const handleApproveClick = (data: TUpdateTransactionStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateTransactionState(data);
    };

    const handleRejectClick = (data: TUpdateTransactionStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateTransactionState(data);
    };

    const handleViewTransaction = (transaction: TTransactionListing) => {
        setSelectedGoldTransaction(transaction);
        setTransactionProductListingModalIsOpen(true);
    };

    const tableHeader = [
        'TRANSACTION ID',
        'TRANSACTION TYPE',
        'EARMARK TRANSACTION',
        'PROJECT NAME',
        'CUSTOMER NAME',
        'CUSTOMER ID TYPE',
        'CUSTOMER ID NUMBER',
        'CIF NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'STATUS',
        'FUND TYPE',
        'GOLD TYPE',
        'SILVER TYPE',
        'BRANCH TELLER NAME',
        'SALES PERSONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
        'ACTION',
    ];

    let prevTransactionId = 0;
    const tableBody = transactionListing.map(transaction => {
        const {
            transactionId,
            isEarmarkTransaction,
            projectName,
            name,
            idType,
            idNo,
            CIFNo,
            branchCode,
            branchName,
            regionName,
            status,
            action,
            fundType,
            goldType,
            silverType,
            createdAt,
            branchTellerName,
            salesPersonnelName,
            referralName,
        } = transaction;

        const renderTransactionId = () => {
            if (prevTransactionId !== transactionId) {
                prevTransactionId = transactionId;
                return transactionId;
            }

            return '-';
        };

        const renderAction = () => {
            if (!action) return '-';

            return TransactionActionEnum[action];
        };

        const metalType =
            goldType
                ? 'gold'
                : silverType
                    ? 'silver'
                    : '';

        return {
            className:
                metalType === 'gold'
                    ? Styles['gold-row']
                    : '',

            cells: [
                renderTransactionId(),
                (
                    <Text
                        key={transactionId}
                        label={renderAction() || ''}
                        className={Styles['transaction-action-label']}
                        isBold
                    />
                ),
                isEarmarkTransaction
                    ? renderBinaryStatusChip(isEarmarkTransaction, 'Yes')
                    : '-',
                projectName || '-',
                name,
                idType ? CustomerIdTypeEnum[idType] : '-',
                idNo,
                CIFNo,
                branchCode,
                branchName,
                regionName,
                renderTransactionStatusChip(status, TransactionStatusEnum[status]),
                fundType ? FundTypeEnum[fundType] : '-',
                goldType
                    ? GoldTypeEnum[goldType].split(/(?=[A-Z])/).join(' ')
                    : '-',
                silverType
                    ? SilverTypeEnum[silverType].split(/(?=[A-Z])/).join(' ')
                    : '-',
                branchTellerName || '-',
                salesPersonnelName || '-',
                referralName || '-',
                dayjs(createdAt).format('DD/MM/YYYY hh:mm A'),
                (
                    <div
                        key={transactionId}
                        className={Styles['action-button-container']}
                    >
                        <ActionButton
                            label='View Details'
                            onClick={() => handleViewTransaction(transaction)}
                        />

                        {hasEditAccess && (
                            <ActionButton
                                testId='approval-buySell-table-approve-button'
                                label='Approve'
                                onClick={() =>
                                    handleApproveClick({
                                        transactionId,
                                        status: TransactionStatusEnum.Approved,
                                    })
                                }
                                className={`${Styles['action-button']} ${Styles.approve}`}
                            />
                        )}

                        {hasEditAccess && (
                            <ActionButton
                                testId='approval-buySell-table-reject-button'
                                label='Reject'
                                onClick={() =>
                                    handleRejectClick({
                                        transactionId,
                                        status: TransactionStatusEnum.Rejected,
                                    })
                                }
                                className={`${Styles['action-button']} ${Styles.reject}`}
                            />
                        )}
                    </div>
                ),
            ],
        };
    });

    return (
        <>
            <Table
                header={tableHeader}
                body={tableBody}
                isLoading={getPendingTransactionListingIsLoading}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </>
    );
};

export default GoldTransactionTableApproval;
